import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  Divider,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { getItem, updateItem } from '../../services/ApiService';
import { Item, UpdateItemRequest } from '../../models/SafetyPortalTypes';
import Spinner from '../../components/Spinner/Spinner';
import AddCheck from '../../components/AddCheck/AddCheck';
import ImageGallery from '../../components/ImageGallery/ImageGallery';
import { format } from 'date-fns';
import styles from './ItemDetails.module.css';

const ItemDetails: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [deactivationNote, setDeactivationNote] = useState('');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const { data: item, isLoading: isLoadingItem, error, refetch } = useQuery<Item>({
    queryKey: ['item', id],
    queryFn: () => {
      if (!id) throw new Error('No ID provided');
      return getItem(id);
    },
    enabled: !!id,
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'checked':
        return '#4caf50';
      case 'overdue':
        return '#f44336';
      case 'pending':
        return '#ff9800';
      default:
        return '#757575';
    }
  };

  const handleCheckAdded = () => {
    refetch();
  };

  const deactivateMutation = useMutation({
    mutationFn: async (data: UpdateItemRequest) => {
      if (!id) throw new Error('No ID provided');
      return updateItem({ ...data, _id: id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['item', id] });
      queryClient.invalidateQueries({ queryKey: ['items'] });
      setDeactivateDialogOpen(false);
      setDeactivationNote('');
      setSnackbar({ open: true, message: t('Items.Messages.DeactivateSuccess') || 'Item deactivated successfully', severity: 'success' });
      refetch();
    },
    onError: () => {
      setSnackbar({ open: true, message: t('Items.Messages.DeactivateError') || 'Failed to deactivate item', severity: 'error' });
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('No ID provided');
      return updateItem({ _id: id, isActive: true });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['item', id] });
      queryClient.invalidateQueries({ queryKey: ['items'] });
      setSnackbar({ open: true, message: t('Items.Messages.ReactivateSuccess') || 'Item reactivated successfully', severity: 'success' });
      refetch();
    },
    onError: () => {
      setSnackbar({ open: true, message: t('Items.Messages.ReactivateError') || 'Failed to reactivate item', severity: 'error' });
    },
  });

  const handleDeactivate = () => {
    if (!id) return;
    deactivateMutation.mutate({
      _id: id,
      isActive: false,
      deactivationNote: deactivationNote.trim() || null,
    });
  };

  const handleReactivate = () => {
    reactivateMutation.mutate();
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  if (isLoadingItem) return <Spinner />;
  if (error || !item) {
    return (
      <div className={styles.container}>
        <Alert severity="error">{t('Items.Messages.LoadError')}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/items')}
          sx={{
            mt: 2,
            '& .MuiButton-startIcon': {
              marginRight: '8px',
              marginLeft: 0,
            },
            '[dir="rtl"] & .MuiButton-startIcon': {
              marginLeft: '8px',
              marginRight: 0,
            },
          }}
        >
          {t('General.Labels.Back')}
        </Button>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/items')}
          variant="outlined"
          sx={{
            backgroundColor: '#ffffff',
            borderColor: '#78B26E',
            color: '#333333',
            '&:hover': {
              backgroundColor: '#E1ECE2',
              borderColor: '#5E8856',
            },
            '& .MuiButton-startIcon': {
              marginRight: '8px',
              marginLeft: 0,
            },
            '[dir="rtl"] & .MuiButton-startIcon': {
              marginLeft: '8px',
              marginRight: 0,
            },
          }}
        >
          {t('General.Labels.Back')}
        </Button>
        <Typography variant="h4">{item.name}</Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Item Information */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('Items.Details.Information')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography>
                <strong>{t('Items.Table.ID')}:</strong> {item.itemId}
              </Typography>
              <Typography>
                <strong>{t('Items.Table.Name')}:</strong> {item.name}
              </Typography>
              <Typography>
                <strong>{t('Items.Table.Location')}:</strong> {item.location || '-'}
              </Typography>
              <Typography>
                <strong>{t('Items.Table.Status')}:</strong>{' '}
                <Chip
                  label={t(`Items.Status.${item.status.charAt(0).toUpperCase() + item.status.slice(1)}`)}
                  size="small"
                  sx={{
                    backgroundColor: getStatusColor(item.status),
                    color: 'white',
                    ml: 1,
                  }}
                />
              </Typography>
              <Typography>
                <strong>{t('Items.Table.LastCheck')}:</strong>{' '}
                {item.lastCheckDate ? format(new Date(item.lastCheckDate), 'dd/MM/yyyy') : '-'}
              </Typography>
              <Typography>
                <strong>{t('Items.Table.NextCheck')}:</strong>{' '}
                {item.nextCheckDate ? format(new Date(item.nextCheckDate), 'dd/MM/yyyy') : '-'}
              </Typography>
              {item.description && (
                <Typography>
                  <strong>{t('Items.Form.Description')}:</strong> {item.description}
                </Typography>
              )}
              {item.notes && (
                <Typography>
                  <strong>{t('Items.Form.Notes')}:</strong> {item.notes}
                </Typography>
              )}
              {item.checkIntervalMonths != null && (
                <Typography>
                  <strong>{t('Items.Form.CheckIntervalMonths') || 'Check Interval (Months)'}:</strong> {item.checkIntervalMonths} {t('Items.Form.Months') || 'months'}
                </Typography>
              )}
              <Typography>
                <strong>{t('Items.Form.Responsible') || 'Responsible'}:</strong> {item.responsible === 'internal' ? (t('Items.Form.ResponsibleInternal') || 'פנימי') : (t('Items.Form.ResponsibleContractor') || 'קבלן חיצוני')}
              </Typography>
              {item.model && (
                <Typography>
                  <strong>{t('Items.Form.Model')}:</strong> {item.model}
                </Typography>
              )}
              {item.licenseNumber && (
                <Typography>
                  <strong>{t('Items.Form.LicenseNumber')}:</strong> {item.licenseNumber}
                </Typography>
              )}
              {item.insuranceDate && (
                <Typography>
                  <strong>{t('Items.Form.InsuranceDate')}:</strong>{' '}
                  {format(new Date(item.insuranceDate), 'dd/MM/yyyy')}
                </Typography>
              )}
              {item.insuranceFile?.downloadUrl && (
                <Box mt={1}>
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                    {t('Items.Form.InsuranceFile')}:
                  </Typography>
                  <img
                    src={item.insuranceFile.downloadUrl}
                    alt={t('Items.Form.InsuranceFile')}
                    style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 4, border: '1px solid #e0e0e0', cursor: 'pointer' }}
                    onClick={() => window.open(item.insuranceFile!.downloadUrl, '_blank')}
                  />
                </Box>
              )}
              {item.licenseExpirationDate && (
                <Typography>
                  <strong>{t('Items.Form.LicenseExpirationDate')}:</strong>{' '}
                  {format(new Date(item.licenseExpirationDate), 'dd/MM/yyyy')}
                </Typography>
              )}
              {item.licenseFile?.downloadUrl && (
                <Box mt={1}>
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                    {t('Items.Form.LicenseFile')}:
                  </Typography>
                  <img
                    src={item.licenseFile.downloadUrl}
                    alt={t('Items.Form.LicenseFile')}
                    style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 4, border: '1px solid #e0e0e0', cursor: 'pointer' }}
                    onClick={() => window.open(item.licenseFile!.downloadUrl, '_blank')}
                  />
                </Box>
              )}
              {/* Display item images */}
              {item.files && item.files.length > 0 && (
                <Box mt={2}>
                  <ImageGallery files={item.files} title={t('Items.Details.ItemImages')} />
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Check History */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('Checks.History')} ({item.checks?.length || 0})
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {item.checks && item.checks.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {item.checks
                  .slice()
                  .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                  .map((check, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 2,
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        backgroundColor: '#f9f9f9',
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        {check.passed ? (
                          <CheckCircleIcon sx={{ color: '#4caf50' }} />
                        ) : (
                          <CancelIcon sx={{ color: '#f44336' }} />
                        )}
                        <Typography variant="subtitle1" fontWeight="bold">
                          {format(new Date(check.date), 'dd/MM/yyyy HH:mm')}
                        </Typography>
                        <Chip
                          label={check.passed ? t('Checks.Passed.Yes') : t('Checks.Passed.No')}
                          size="small"
                          color={check.passed ? 'success' : 'error'}
                          sx={{ ml: 'auto' }}
                        />
                      </Box>
                      {check.notes && (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          {check.notes}
                        </Typography>
                      )}
                      {/* Display check images */}
                      {check.files && check.files.length > 0 && (
                        <Box mt={1}>
                          <ImageGallery files={check.files} title={t('Checks.Images')} />
                        </Box>
                      )}
                    </Box>
                  ))}
              </Box>
            ) : (
              <Typography color="text.secondary">{t('Checks.NoChecks')}</Typography>
            )}
          </Paper>
        </Grid>

        {/* Add Check Form */}
        <Grid item xs={12}>
          <AddCheck itemId={item._id} onCheckAdded={handleCheckAdded} />
        </Grid>

        {/* Deactivation/Reactivation Section */}
        <Grid item xs={12}>
          {item.isActive ? (
            <Paper sx={{ p: 3, mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                {t('Items.Deactivate.Title') || 'Deactivate Item'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('Items.Deactivate.Description') || 'Instead of deleting, you can mark this item as inactive. It will be hidden from default views but can be reactivated later.'}
              </Typography>
              <Button
                variant="contained"
                color="error"
                onClick={() => setDeactivateDialogOpen(true)}
              >
                {t('Items.Deactivate.Button') || 'Mark as Inactive'}
              </Button>
            </Paper>
          ) : (
            <Paper sx={{ p: 3, mt: 2, backgroundColor: '#ffe6e6', border: '1px solid #ffcccc' }}>
              <Typography variant="h6" gutterBottom color="error">
                ⚠️ {t('Items.Deactivate.InactiveTitle') || 'Item is Inactive'}
              </Typography>
              {item.deactivationNote && (
                <Typography sx={{ mb: 2 }}>
                  <strong>{t('Items.Deactivate.Reason') || 'Reason'}:</strong> {item.deactivationNote}
                </Typography>
              )}
              <Button
                variant="contained"
                color="success"
                onClick={handleReactivate}
                disabled={reactivateMutation.isPending}
              >
                {reactivateMutation.isPending ? t('General.Labels.Loading') || 'Loading...' : t('Items.Deactivate.Reactivate') || 'Reactivate Item'}
              </Button>
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* Deactivation Dialog */}
      <Dialog open={deactivateDialogOpen} onClose={() => setDeactivateDialogOpen(false)}>
        <DialogTitle>{t('Items.Deactivate.DialogTitle') || 'Deactivate Item'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('Items.Deactivate.DialogDescription') || 'Enter an optional reason for deactivating this item:'}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={deactivationNote}
            onChange={(e) => setDeactivationNote(e.target.value)}
            placeholder={t('Items.Deactivate.NotePlaceholder') || 'Optional: Enter reason for deactivation...'}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateDialogOpen(false)}>
            {t('General.Labels.Cancel')}
          </Button>
          <Button
            onClick={handleDeactivate}
            variant="contained"
            color="error"
            disabled={deactivateMutation.isPending}
          >
            {deactivateMutation.isPending ? t('General.Labels.Loading') || 'Loading...' : t('Items.Deactivate.Confirm') || 'Deactivate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ItemDetails;

